import { selectWorkspace } from "@shared/slices/common.slice.ts";
import { SubGroup, WorkspaceType } from "@shared/types";
import { useAdminGroupsSelect } from "features/admin/hooks.ts";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

interface useSignalsSubGroupSelectorProps {
  subGroupId?: number;
  subGroups: SubGroup[] | undefined;
  setSubGroupId: (subGroupId: number) => void;
}

const useSignalsSubGroupSelector = ({
  subGroups,
  subGroupId,
  setSubGroupId,
}: useSignalsSubGroupSelectorProps) => {
  const workspace = useSelector(selectWorkspace);
  const isAdminWorkspace = workspace === WorkspaceType.ADMIN;
  const { updateUrl, groupsFilterSectionData, querySubGroupId } =
    useAdminGroupsSelect({});
  const [filtersSubGroupId] = groupsFilterSectionData.value;
  const defaultAdminSubGroupId = querySubGroupId ?? filtersSubGroupId;

  const adminSelectorSubGroupId = useMemo(
    () =>
      isAdminWorkspace && defaultAdminSubGroupId
        ? subGroups?.find((group) => group.id === defaultAdminSubGroupId)?.id
        : undefined,
    [subGroups, isAdminWorkspace, defaultAdminSubGroupId]
  );

  useEffect(() => {
    if (
      isAdminWorkspace &&
      subGroupId &&
      adminSelectorSubGroupId &&
      subGroupId !== adminSelectorSubGroupId
    ) {
      setSubGroupId(adminSelectorSubGroupId);
    }
  }, [adminSelectorSubGroupId]);

  const onSubGroupChange = (subGroupId: number) => {
    setSubGroupId(subGroupId);
    if (isAdminWorkspace) {
      updateUrl(subGroupId);
      groupsFilterSectionData.onChange([subGroupId]);
    }
  };

  return {
    onSubGroupChange,
    isAdminWorkspace,
    adminSelectorSubGroupId,
  };
};

export default useSignalsSubGroupSelector;
